'use client';

import React, { useState } from 'react';
import {
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Typography,
  Box,
  Tooltip,
  Divider
} from '@mui/material';
import WechatBindDialog from '../WechatBindDialog';
import {
  MonetizationOn,
  Lock,
  Phone,
  Logout,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// 用户接口定义
interface User {
  id: number;
  username: string;
  display_name?: string;
  email?: string;
  role: string;
  organization_name?: string;
  organization_id?: number;
}

// 购买记录接口定义
interface PurchaseRecord {
  subscriptionId: string;
  orderNumber: string;
  planName: string;
  planCode: string;
  startDate: string;
  endDate: string;
  daysRemaining: number;
  projectCreditsTotal: number;
  projectCreditsUsed: number;
  projectCreditsRemaining: number;
  keyProjectCreditsTotal?: number;       // 重点项目总配额
  keyProjectCreditsUsed?: number;        // 重点项目已使用
  projectTargetCreditsTotal?: number;    // 项目目标总配额
  projectTargetCreditsUsed?: number;     // 项目目标已使用
  departmentTargetCreditsTotal?: number; // 部门目标总配额
  departmentTargetCreditsUsed?: number;  // 部门目标已使用
  projectMonitoringCreditsTotal?: number;   // 项目监控总配额
  projectMonitoringCreditsUsed?: number;    // 项目监控已使用
  departmentMonitoringCreditsTotal?: number;// 部门监控总配额
  departmentMonitoringCreditsUsed?: number; // 部门监控已使用
  projectSelfEvaluationCreditsTotal?: number;   // 项目自评总配额
  projectSelfEvaluationCreditsUsed?: number;    // 项目自评已使用
  departmentSelfEvaluationCreditsTotal?: number; // 部门自评总配额
  departmentSelfEvaluationCreditsUsed?: number;  // 部门自评已使用
  maxUsers: number;
  currentUsers: number;
  amount: number;  // 支付金额（分为单位）
  paymentMethod: string;  // 支付方式
  paymentTime: string;  // 支付时间
  isTrial: boolean;
  subscriptionStatus: string;  // 订阅状态：active, expired, cancelled
  paymentStatus: string;  // 支付状态：completed, pending, failed
}

// 密码修改表单数据接口
interface PasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// 获取当前登录用户的信息
const getCurrentUser = (): User | null => {
  try {
    const userStr = localStorage.getItem('currentUser');
    return userStr ? JSON.parse(userStr) : null;
  } catch (e) {
    console.error("Error parsing current user from localStorage:", e);
    return null;
  }
};

// 获取认证token
const getAuthToken = (): string | null => {
  try {
    return localStorage.getItem('auth_token');
  } catch (e) {
    console.error("Error getting auth token:", e);
    return null;
  }
};

// 创建带认证头的fetch请求
const fetchWithAuth = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    ...options,
    headers,
  });
};

export default function UserAvatar(): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState<boolean>(false);
  const [purchaseHistoryOpen, setPurchaseHistoryOpen] = useState<boolean>(false);
  const [purchaseHistory, setPurchaseHistory] = useState<PurchaseRecord[]>([]);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
  const [passwordData, setPasswordData] = useState<PasswordData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [wechatBindOpen, setWechatBindOpen] = useState<boolean>(false);

  const user = getCurrentUser();
  const navigate = useNavigate();

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleChangePassword = () => {
    setPasswordDialogOpen(true);
    handleMenuClose();
  };

  const handlePurchaseHistory = async () => {
    setPurchaseHistoryOpen(true);
    handleMenuClose();
    
    // 获取购买记录
    await fetchPurchaseHistory();
  };

  const handleWechatBind = () => {
    setWechatBindOpen(true);
    handleMenuClose();
  };

  // 时间格式化函数
  const formatPaymentTime = (timeStr: string) => {
    if (!timeStr) return '';
    try {
      const date = new Date(timeStr);
      // 加8小时转换为北京时间
      const beijingTime = new Date(date.getTime() + 8 * 60 * 60 * 1000);
      return beijingTime.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return timeStr;
    }
  };

  const formatDateOnly = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // 获取订阅状态显示信息
  const getSubscriptionStatusInfo = (record: PurchaseRecord) => {
    if (!record.endDate) {
      return {
        text: '未知',
        color: 'text.secondary',
        backgroundColor: '#f5f5f5',
        borderColor: '#e0e0e0'
      };
    }
    
    // 只比较日期部分，不考虑时间
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // 解析结束日期（可能是 "2025-12-15" 格式）
    let isExpired = false;
    try {
      const endDateStr = record.endDate;
      const endDateParts = endDateStr.split(/[-/]/);
      if (endDateParts.length >= 3) {
        const endYear = parseInt(endDateParts[0], 10);
        const endMonth = parseInt(endDateParts[1], 10) - 1; // 月份从0开始
        const endDay = parseInt(endDateParts[2], 10);
        const endDate = new Date(endYear, endMonth, endDay);
        
        // 如果结束日期小于今天，才算过期（结束日期当天不算过期）
        isExpired = endDate < today;
      } else {
        // 如果解析失败，使用原来的逻辑作为后备
        const endDate = new Date(record.endDate);
        isExpired = endDate < now;
      }
    } catch (e) {
      // 解析失败，使用原来的逻辑作为后备
      try {
        const endDate = new Date(record.endDate);
        isExpired = endDate < now;
      } catch (e2) {
        // 完全无法解析，默认不过期
        isExpired = false;
      }
    }
    
    // 首先检查是否已到期（无论数据库状态如何）
    if (isExpired) {
      return {
        text: '已过期',
        color: 'warning.main',
        backgroundColor: '#fff3e0',
        borderColor: '#ff9800'
      };
    }
    
    // 然后根据数据库中的订阅状态判断
    switch (record.subscriptionStatus) {
      case 'active':
        return {
          text: '使用中',
          color: 'success.main',
          backgroundColor: '#e8f5e8',
          borderColor: '#4caf50'
        };
      case 'cancelled':
        return {
          text: '已取消',
          color: 'error.main',
          backgroundColor: '#ffebee',
          borderColor: '#f44336'
        };
      case 'expired':
        return {
          text: '已过期',
          color: 'warning.main',
          backgroundColor: '#fff3e0',
          borderColor: '#ff9800'
        };
      case 'pending':
        return {
          text: '待激活',
          color: 'info.main',
          backgroundColor: '#e3f2fd',
          borderColor: '#2196f3'
        };
      default:
        return {
          text: `未知状态(${record.subscriptionStatus || 'null'})`,
          color: 'text.secondary',
          backgroundColor: '#f5f5f5',
          borderColor: '#e0e0e0'
        };
    }
  };

  const fetchPurchaseHistory = async () => {
    if (!user?.username) return;
    
    setLoadingHistory(true);
    try {
      const response = await fetchWithAuth(`/api/v1/users/purchase-history?username=${encodeURIComponent(user.username)}`);
      const data = await response.json();
      
      if (response.ok && data.success) {
        setPurchaseHistory(data.data?.purchase_records || []);
      } else {
        setPurchaseHistory([]);
      }
    } catch (error) {
      setPurchaseHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetchWithAuth('/api/v1/auth/logout', { method: 'POST' });
      localStorage.removeItem('auth_token');
      localStorage.removeItem('username');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('session_id');
      window.location.href = '/login';
    } catch (error) {
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    // 清除错误信息
    if (error) setError('');
  };

  const handlePasswordSubmit = async () => {
    setError('');
    setSuccess('');

    // 验证输入
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setError('请填写所有字段');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('新密码和确认密码不匹配');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('新密码长度至少6位');
      return;
    }

    try {
      const response = await fetchWithAuth('/api/v1/users/change-password', {
        method: 'POST',
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '修改密码失败');
      }

      setSuccess('密码修改成功！');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // 3秒后关闭对话框
      setTimeout(() => {
        setPasswordDialogOpen(false);
        setSuccess('');
      }, 3000);

    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setError('');
    setSuccess('');
  };

  // 获取显示名称的首字母作为头像
  const getInitials = (displayName?: string): string => {
    if (!displayName) return 'U';
    return displayName.charAt(0).toUpperCase();
  };

  // 角色显示转换函数
  const getRoleDisplayName = (role: string): string => {
    const roleMap: Record<string, string> = {
      'super_admin': '超级管理员',
      'admin': '管理员',
      'zhaoyuan_admin': '昭元管理员',
      'writer': '写作员'
    };
    return roleMap[role] || role;
  };

  return (
    <>
      <Tooltip title={user?.display_name || user?.username || '用户'}>
        <IconButton
          onClick={handleAvatarClick}
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.15)',
            color: 'white',
            p: 1,
            borderRadius: '50%',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.25)'
            }
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontSize: '0.9rem',
              fontWeight: 'bold'
            }}
          >
            {getInitials(user?.display_name)}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="body2" fontWeight="bold">
            {user?.display_name || user?.username}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.username !== user?.display_name && `(${user?.username})`}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {getRoleDisplayName(user?.role || 'writer')}
          </Typography>
        </Box>
        
        <Divider />

        {/* 系统设置 - 只有管理员可见 */}
        {user?.role && ['super_admin', 'admin', 'zhaoyuan_admin'].includes(user.role) && (
          <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon fontSize="small" />
              <Typography variant="body2">系统设置</Typography>
            </Box>
          </MenuItem>
        )}

        <Divider />

        <MenuItem onClick={handlePurchaseHistory}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MonetizationOn fontSize="small" />
            <Typography variant="body2">购买记录</Typography>
          </Box>
        </MenuItem>

        <MenuItem onClick={handleChangePassword}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Lock fontSize="small" />
            <Typography variant="body2">修改密码</Typography>
          </Box>
        </MenuItem>

        <MenuItem onClick={handleWechatBind}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Phone fontSize="small" />
            <Typography variant="body2">绑定微信</Typography>
          </Box>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogout}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Logout fontSize="small" />
            <Typography variant="body2">退出登录</Typography>
          </Box>
        </MenuItem>
      </Menu>

      {/* 修改密码对话框 */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={handleClosePasswordDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>修改密码</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          
          <TextField
            autoFocus
            margin="dense"
            name="currentPassword"
            label="当前密码"
            type="password"
            fullWidth
            variant="outlined"
            value={passwordData.currentPassword}
            onChange={handlePasswordChange}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="newPassword"
            label="新密码"
            type="password"
            fullWidth
            variant="outlined"
            value={passwordData.newPassword}
            onChange={handlePasswordChange}
            required
            helperText="密码长度至少6位"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="confirmPassword"
            label="确认新密码"
            type="password"
            fullWidth
            variant="outlined"
            value={passwordData.confirmPassword}
            onChange={handlePasswordChange}
            required
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog}>取消</Button>
          <Button 
            onClick={handlePasswordSubmit} 
            variant="contained"
            disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
          >
            确认修改
          </Button>
        </DialogActions>
      </Dialog>

      {/* 微信绑定对话框 */}
      <WechatBindDialog 
        open={wechatBindOpen} 
        onClose={() => setWechatBindOpen(false)}
      />

      {/* 购买记录对话框 */}
      <Dialog 
        open={purchaseHistoryOpen} 
        onClose={() => setPurchaseHistoryOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">购买记录</Typography>
            <IconButton onClick={() => setPurchaseHistoryOpen(false)}>
              <Typography variant="h6">×</Typography>
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Typography>加载中...</Typography>
            </Box>
          ) : purchaseHistory.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Typography color="text.secondary">暂无购买记录</Typography>
            </Box>
          ) : (
            <Box sx={{ mb: 3 }}>
              {purchaseHistory.map((subscription) => {
                const statusInfo = getSubscriptionStatusInfo(subscription);
                const isExpiredOrCancelled = statusInfo.text === '已过期' || statusInfo.text === '已取消';
                
                return (
                  <Box key={subscription.subscriptionId} sx={{ 
                    border: `2px solid ${statusInfo.borderColor}`, 
                    borderRadius: 2, 
                    p: 2, 
                    mb: 2,
                    backgroundColor: isExpiredOrCancelled ? statusInfo.backgroundColor : '#fafafa',
                    position: 'relative'
                  }}>
                    {/* 订单状态 */}
                    <Box sx={{ mb: 1 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: statusInfo.color,
                          fontWeight: 'bold',
                          display: 'inline-block',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: statusInfo.backgroundColor,
                          border: `1px solid ${statusInfo.borderColor}`
                        }}
                      >
                        订单状态: {statusInfo.text}
                      </Typography>
                    </Box>
                    
                    {/* 订单号 */}
                    {!subscription.isTrial && subscription.orderNumber && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        订单号: {subscription.orderNumber}
                      </Typography>
                    )}
                    
                    {/* 套餐类型 */}
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'medium' }}>
                      套餐类型: {subscription.planName}
                    </Typography>
                  
                  {/* 使用期限 */}
                  {subscription.startDate && subscription.endDate && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      使用期限: {formatDateOnly(subscription.startDate)} 至 {formatDateOnly(subscription.endDate)}
                      {subscription.daysRemaining > 0 && (
                        <Typography component="span" color="success.main" sx={{ ml: 1 }}>
                          (剩余 {subscription.daysRemaining} 天)
                        </Typography>
                      )}
                    </Typography>
                  )}

                  {/* 项目配额 - 显示各类配额 */}
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      项目配额:
                    </Typography>
                    {subscription.keyProjectCreditsTotal !== undefined && (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        重点项目: {subscription.keyProjectCreditsUsed || 0}/{subscription.keyProjectCreditsTotal}
                      </Typography>
                    )}
                    {subscription.projectTargetCreditsTotal !== undefined && (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        项目目标: {subscription.projectTargetCreditsUsed || 0}/{subscription.projectTargetCreditsTotal}
                      </Typography>
                    )}
                    {subscription.departmentTargetCreditsTotal !== undefined && (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        部门目标: {subscription.departmentTargetCreditsUsed || 0}/{subscription.departmentTargetCreditsTotal}
                      </Typography>
                    )}
                    {subscription.projectMonitoringCreditsTotal !== undefined && subscription.projectMonitoringCreditsTotal > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        项目监控: {subscription.projectMonitoringCreditsUsed || 0}/{subscription.projectMonitoringCreditsTotal}
                      </Typography>
                    )}
                    {subscription.departmentMonitoringCreditsTotal !== undefined && subscription.departmentMonitoringCreditsTotal > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        部门监控: {subscription.departmentMonitoringCreditsUsed || 0}/{subscription.departmentMonitoringCreditsTotal}
                      </Typography>
                    )}
                    {subscription.projectSelfEvaluationCreditsTotal !== undefined && (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        项目自评: {subscription.projectSelfEvaluationCreditsUsed || 0}/{subscription.projectSelfEvaluationCreditsTotal}
                      </Typography>
                    )}
                    {subscription.departmentSelfEvaluationCreditsTotal !== undefined && (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        部门自评: {subscription.departmentSelfEvaluationCreditsUsed || 0}/{subscription.departmentSelfEvaluationCreditsTotal}
                      </Typography>
                    )}
                  </Box>
                  
                  {/* 账号数量 - 试用套餐不显示 */}
                  {!subscription.isTrial && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      账号数量: {subscription.maxUsers}个(已使用 {subscription.currentUsers}个, 剩余 {subscription.maxUsers - subscription.currentUsers}个)
                    </Typography>
                  )}
                  
                  {/* 付款金额 */}
                  {subscription.amount > 0 && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      付款金额: {(subscription.amount / 100).toFixed(2)}元
                    </Typography>
                  )}
                  
                  {/* 支付方式 - 试用套餐不显示 */}
                  {!subscription.isTrial && subscription.paymentMethod && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      支付方式: {subscription.paymentMethod}
                    </Typography>
                  )}
                  
                  {/* 支付时间 */}
                  {subscription.paymentTime && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      支付时间: {formatPaymentTime(subscription.paymentTime)}
                    </Typography>
                  )}
                  </Box>
                );
              })}
            </Box>
          )}
         
          {/* 分页信息 */}
          {purchaseHistory.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                第1条 - 第{purchaseHistory.length}条, 共{purchaseHistory.length}条
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton size="small" disabled>
                  <Typography variant="caption">←</Typography>
                </IconButton>
                <IconButton size="small" disabled>
                  <Typography variant="caption">→</Typography>
                </IconButton>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
